import { useState, useRef, useCallback, useEffect } from "react";
import { Camera, VideoCamera, Record, Stop, Warning, Broadcast, GearSix, WifiHigh, Eyeglasses, ShieldCheck, CircleNotch } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CameraFeedPage() {
  const [streaming, setStreaming] = useState(false);
  const [cameraType, setCameraType] = useState("phone");
  const [facingMode, setFacingMode] = useState("environment");
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [snapshots, setSnapshots] = useState([]);
  const [inspecting, setInspecting] = useState(false);
  const [inspectResult, setInspectResult] = useState(null);
  const [inspectTrade, setInspectTrade] = useState("electrical");
  const [inspectType, setInspectType] = useState("rough_in");

  const startStream = useCallback(async () => {
    setError(null);
    try {
      const constraints = {
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      streamRef.current = stream;
      setStreaming(true);
    } catch (err) {
      setError(err.name === "NotAllowedError" ? "Camera permission denied. Please allow camera access." :
               err.name === "NotFoundError" ? "No camera found on this device." :
               `Camera error: ${err.message}`);
    }
  }, [facingMode]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreaming(false);
  }, []);

  const takeSnapshot = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setSnapshots(prev => [{ id: Date.now(), url: dataUrl, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
  }, []);

  const inspectFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    setInspecting(true);
    setInspectResult(null);
    try {
      canvas.toBlob(async (blob) => {
        const formData = new FormData();
        formData.append("trade", inspectTrade);
        formData.append("jurisdiction", inspectTrade === "electrical" ? "NEC 2023" : "IMC 2021");
        formData.append("inspection_type", inspectType);
        formData.append("description", `Live video frame inspection - ${inspectType}`);
        formData.append("frame", blob, "frame.jpg");
        try {
          const res = await axios.post(`${API}/inspect/video-frame`, formData, { headers: { "Content-Type": "multipart/form-data" } });
          setInspectResult(res.data);
          toast.success("Frame inspected");
        } catch { toast.error("Inspection failed"); }
        setInspecting(false);
      }, "image/jpeg", 0.8);
    } catch { setInspecting(false); toast.error("Failed to capture frame"); }
  }, [inspectTrade, inspectType]);

  const [ipCamUrl, setIpCamUrl] = useState("");
  const [ipConnected, setIpConnected] = useState(false);
  const [vehicleCam, setVehicleCam] = useState(false);
  const ipVideoRef = useRef(null);

  const connectIpCam = () => {
    if (!ipCamUrl.trim()) { return; }
    setIpConnected(true);
  };

  const flipCamera = useCallback(() => {
    const newMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newMode);
    if (streaming) { stopStream(); setTimeout(() => startStream(), 300); }
  }, [facingMode, streaming, stopStream, startStream]);

  useEffect(() => { return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); }; }, []);

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="camera-feed-page">
      <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Camera Feed
      </h1>
      <p className="text-xs text-[#404040] mb-2 font-mono-data">BODY CAM / SMART GLASSES / PHONE</p>

      {/* Disclaimer */}
      <div className="p-2.5 bg-[#F4F4F5] border border-[#E5E5E5] rounded-sm mb-4">
        <p className="text-[10px] text-[#404040] leading-relaxed">
          <b>Disclaimer:</b> Video AI inspection is for <b>reference only</b>. It does not replace 
          a certified inspection by the Authority Having Jurisdiction. WorkPro assumes no liability 
          for code compliance decisions based on video analysis.
        </p>
      </div>

      {/* Camera Type Selector */}
      <div className="flex gap-2 mb-4">
        {[
          { id: "phone", label: "Phone Cam", icon: Camera },
          { id: "bodycam", label: "Body Cam", icon: VideoCamera },
          { id: "glasses", label: "Smart Glasses", icon: Eyeglasses },
          { id: "vehicle", label: "Vehicle Cam", icon: Broadcast },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`cam-type-${id}`}
            onClick={() => setCameraType(id)}
            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-sm border text-xs font-bold transition-colors ${
              cameraType === id
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-[#404040] border-[#E5E5E5] hover:border-[#0A0A0A]"
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      {/* Camera Feed */}
      <div className="camera-hud rounded-sm overflow-hidden mb-4 relative" style={{ minHeight: 240 }}>
        {streaming ? (
          <>
            <video ref={videoRef} className="w-full h-auto" playsInline muted autoPlay />
            {/* HUD Overlay */}
            <div className="absolute top-2 left-2 glass-overlay px-2 py-1 rounded-sm flex items-center gap-1">
              <Record size={8} className="text-red-500" />
              <span className="text-[10px] text-white font-mono-data">
                {cameraType.toUpperCase()} | LIVE
              </span>
            </div>
            <div className="absolute top-2 right-2 glass-overlay px-2 py-1 rounded-sm">
              <span className="text-[10px] text-[#00E5FF] font-mono-data">
                {new Date().toLocaleTimeString()}
              </span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-60 text-[#404040]">
            {cameraType === "phone" ? (
              <Camera size={48} className="text-[#E5E5E5] mb-3" />
            ) : cameraType === "bodycam" ? (
              <VideoCamera size={48} className="text-[#E5E5E5] mb-3" />
            ) : (
              <Eyeglasses size={48} className="text-[#E5E5E5] mb-3" />
            )}
            <p className="text-sm">{cameraType === "phone" ? "Tap Start to use phone camera" :
              cameraType === "bodycam" ? "Connect body cam via WiFi or use phone camera" :
              "Connect smart glasses via WiFi or use phone camera"}</p>
            {(cameraType === "bodycam" || cameraType === "glasses") && (
              <p className="text-[10px] text-[#404040] mt-2 max-w-xs text-center">
                For {cameraType === "bodycam" ? "body cameras" : "smart glasses"}, the device streams video over WiFi to this interface.
                Phone camera is used as the default feed.
              </p>
            )}
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center p-4">
              <Warning size={32} className="text-[#FF5A00] mx-auto mb-2" />
              <p className="text-xs text-white">{error}</p>
            </div>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />

      {/* Controls */}
      <div className="flex gap-3 mb-4">
        {!streaming ? (
          <button
            data-testid="start-camera-btn"
            onClick={startStream}
            className="flex-1 py-3 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors flex items-center justify-center gap-2"
          >
            <Broadcast size={16} /> Start Feed
          </button>
        ) : (
          <>
            <button
              data-testid="snapshot-btn"
              onClick={takeSnapshot}
              className="flex-1 py-3 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors flex items-center justify-center gap-2"
            >
              <Camera size={16} /> Snap
            </button>
            <button
              data-testid="inspect-frame-btn"
              onClick={inspectFrame}
              disabled={inspecting}
              className="flex-1 py-3 bg-[#34C759] text-white text-sm font-bold rounded-sm hover:bg-[#2AA04A] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {inspecting ? <CircleNotch size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              {inspecting ? "..." : "AI Inspect"}
            </button>
            <button
              data-testid="flip-camera-btn"
              onClick={flipCamera}
              className="px-3 py-3 border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] transition-colors"
            >
              <GearSix size={16} />
            </button>
            <button
              data-testid="stop-camera-btn"
              onClick={stopStream}
              className="px-3 py-3 bg-[#FF5A00] text-white rounded-sm hover:bg-[#CC4800] transition-colors"
            >
              <Stop size={16} />
            </button>
          </>
        )}
      </div>

      {/* Video Inspection Settings (when streaming) */}
      {streaming && (
        <div className="flex gap-2 mb-4" data-testid="inspect-settings">
          <Select value={inspectTrade} onValueChange={setInspectTrade}>
            <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="electrical">Electrical</SelectItem>
              <SelectItem value="hvac">HVAC</SelectItem>
            </SelectContent>
          </Select>
          <Select value={inspectType} onValueChange={setInspectType}>
            <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rough_in">Rough-In</SelectItem>
              <SelectItem value="final">Final</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="general">General</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Inspection Result */}
      {inspectResult && (
        <div className="wp-card mb-4 animate-slide-up" data-testid="video-inspect-result">
          <div className="flex items-center gap-2 mb-2">
            <span className={`font-bold text-sm ${inspectResult.status === "PASS" ? "text-[#34C759]" : inspectResult.status === "FAIL" ? "text-[#FF5A00]" : "text-[#FFCC00]"}`}>
              {inspectResult.status || "CHECK"}
            </span>
            <span className="font-mono-data text-[10px] text-[#404040]">{inspectTrade.toUpperCase()} | {inspectType.toUpperCase()}</span>
          </div>
          {inspectResult.issues?.length > 0 && (
            <ul className="space-y-1 mb-2">{inspectResult.issues.map((iss,i) => <li key={i} className="text-[11px] text-[#404040] flex items-start gap-1"><Warning size={10} className="text-[#FF5A00] mt-0.5 shrink-0" />{iss}</li>)}</ul>
          )}
          {inspectResult.notes && <p className="text-xs text-[#404040]">{inspectResult.notes}</p>}
          {inspectResult.raw_response && !inspectResult.status && <p className="text-xs text-[#404040] whitespace-pre-wrap">{inspectResult.raw_response}</p>}
        </div>
      )}

      {/* IP Camera / WiFi Stream Input */}
      {(cameraType === "bodycam" || cameraType === "glasses" || cameraType === "vehicle") && (
        <div className="wp-card mb-4" data-testid="ip-camera-section">
          <h3 className="font-mono-data text-[10px] text-[#002FA7] mb-2">WIFI STREAM / IP CAMERA</h3>
          <p className="text-[11px] text-[#404040] mb-2">
            Enter the IP camera stream URL from your {cameraType === "vehicle" ? "dashcam" : cameraType === "bodycam" ? "body camera" : "smart glasses"}.
          </p>
          <div className="flex gap-2 mb-2">
            <input
              data-testid="ip-cam-url-input"
              type="text"
              placeholder="rtsp://192.168.1.x:554/stream or http://..."
              value={ipCamUrl}
              onChange={e => setIpCamUrl(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <button
              data-testid="connect-ip-cam-btn"
              onClick={connectIpCam}
              disabled={!ipCamUrl.trim()}
              className="px-3 py-2 text-xs font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C] disabled:opacity-40"
            >
              Connect
            </button>
          </div>
          {ipConnected && (
            <div className="camera-hud rounded-sm overflow-hidden relative" style={{ minHeight: 180 }}>
              <img
                ref={ipVideoRef}
                src={ipCamUrl}
                alt="IP Camera Stream"
                className="w-full h-48 object-cover"
                onError={() => { setIpConnected(false); }}
              />
              <div className="absolute top-2 left-2 glass-overlay px-2 py-1 rounded-sm flex items-center gap-1">
                <WifiHigh size={8} className="text-[#00E5FF]" />
                <span className="text-[10px] text-white font-mono-data">IP STREAM</span>
              </div>
            </div>
          )}
          <div className="text-[10px] text-[#404040] mt-2 space-y-0.5">
            <p><b>Common URLs:</b></p>
            <p>GoPro: http://10.5.5.9:8080/live/amba.m3u8</p>
            <p>Wyze: rtsp://192.168.x.x/live</p>
            <p>RTSP Generic: rtsp://user:pass@IP:554/stream</p>
          </div>
        </div>
      )}

      {/* Vehicle Camera / Insurance Section */}
      {cameraType === "vehicle" && (
        <div className="wp-card mb-4 border-l-2 border-l-[#34C759]" data-testid="vehicle-cam-section">
          <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Vehicle Camera Program</h3>
          <p className="text-[11px] text-[#404040] mb-3 leading-relaxed">
            Vehicle cameras can qualify your fleet for insurance discounts. Safe driving footage may reduce premiums for:
          </p>
          <div className="space-y-2 text-xs">
            <div className="flex items-start gap-2 p-2 bg-[#34C75908] rounded-sm">
              <CheckCircle size={14} className="text-[#34C759] shrink-0 mt-0.5" />
              <div><b>Auto Insurance</b> - Safe driving discounts up to 15% with verified dashcam footage</div>
            </div>
            <div className="flex items-start gap-2 p-2 bg-[#34C75908] rounded-sm">
              <CheckCircle size={14} className="text-[#34C759] shrink-0 mt-0.5" />
              <div><b>Workers' Compensation</b> - Documented safety compliance can lower experience modification rates</div>
            </div>
            <div className="flex items-start gap-2 p-2 bg-[#34C75908] rounded-sm">
              <CheckCircle size={14} className="text-[#34C759] shrink-0 mt-0.5" />
              <div><b>Health Insurance</b> - Wellness/safety programs with camera-verified compliance for group rate discounts</div>
            </div>
          </div>
          <p className="text-[10px] text-[#404040] mt-2 italic">Contact your insurance provider with fleet camera documentation for applicable discounts.</p>
        </div>
      )}

      {/* Device Info */}
      <div className="wp-card mb-4">
        <h3 className="font-mono-data text-[10px] text-[#002FA7] mb-2">COMPATIBLE DEVICES</h3>
        <div className="space-y-2 text-[11px] text-[#404040]">
          <div className="flex items-start gap-2">
            <VideoCamera size={14} className="text-[#002FA7] shrink-0 mt-0.5" />
            <div><b className="text-[#0A0A0A]">Body Cameras:</b> Axon Body 4, Motorola Si500, GoPro Hero (WiFi streaming mode)</div>
          </div>
          <div className="flex items-start gap-2">
            <Eyeglasses size={14} className="text-[#002FA7] shrink-0 mt-0.5" />
            <div><b className="text-[#0A0A0A]">Smart Glasses:</b> Ray-Ban Meta, Vuzix Blade 2, RealWear Navigator Z1 (industrial)</div>
          </div>
          <div className="flex items-start gap-2">
            <WifiHigh size={14} className="text-[#002FA7] shrink-0 mt-0.5" />
            <div><b className="text-[#0A0A0A]">Safety Helmets:</b> DAQRI Smart Helmet, RealWear HMT-1 (hands-free, voice-controlled)</div>
          </div>
        </div>
      </div>

      {/* Snapshots Gallery */}
      {snapshots.length > 0 && (
        <div data-testid="snapshot-gallery">
          <h3 className="font-mono-data text-[10px] text-[#404040] mb-2">SNAPSHOTS ({snapshots.length})</h3>
          <div className="grid grid-cols-3 gap-2">
            {snapshots.map(s => (
              <div key={s.id} className="relative">
                <img src={s.url} alt="Snapshot" className="w-full h-20 object-cover rounded-sm border border-[#E5E5E5]" />
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[8px] text-white text-center font-mono-data py-0.5">
                  {s.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
